/**
 * The browser's mirror of the server's Ken Burns templates.
 *
 * These are not look-alikes. `Motion` in the shared library is two ffmpeg `zoompan` expressions
 * (`compose/Composition.kt:84-130`), and both are closed-form functions of the OUTPUT FRAME INDEX,
 * which the frame grid already gives us:
 *
 *   SLOW_ZOOM  z='min(zoom+0.0015,1.15)':x='iw/2-(iw/zoom/2)':y='ih/2-(ih/zoom/2)'
 *   PAN        z=1.15:x='(iw-iw/zoom)*(on/{d})':y='(ih-ih/zoom)/2'
 *
 * so the preview can show the SAME motion rather than an impression of it. The pair of constants
 * below is the whole contract, and `motion.spec.ts` reads the templates back out of the Kotlin
 * file: a preview that quietly disagreed with the render is worse than one that admits it is
 * approximate.
 *
 * THIS MIRROR FOUND A SERVER DEFECT AND THEN FOLLOWED THE FIX. Until 2026-09-16 SLOW_ZOOM wrote
 * neither `x` nor `y`; zoompan's default for both is the literal `0`, so the crop window's origin
 * stayed pinned to the source's top-left corner and the framing walked off toward the corner as it
 * shrank (measured here at 7.5 % of the frame at the cap, and on the server at 5.2 px of 64 with a
 * centred marker). The mirror could only agree with the render by reproducing that, so it did, and
 * said so -- and `Composition.kt d787c4b` then centred the anchor. This file now mirrors the
 * CENTRED window, which is both what the render does and what §4.2 always promised.
 *
 * TWO MORE THINGS THE TEMPLATES SAY THAT A READING OF THEM DOES NOT, both MEASURED against the
 * shipped ffmpeg (N-126280) by pushing a 200x200 luma-ramp still through each template and
 * recovering the crop window from the output frames by linear fit:
 *
 *   1. THE SLOW ZOOM IS ALREADY MOVING ON FRAME 0. `zoom` inside the expression is the PREVIOUS
 *      frame's value, which zoompan seeds at 1, so the first output frame is 1 + 0.0015 and
 *      z(n) = min(1 + 0.0015*(n+1), 1.15). Measured: ffmpeg crops 199 px of 200 on frame 0
 *      (z = 1.0015) and 173 px on frame 99 (z = 1.15, the cap, reached exactly there).
 *      `1 + 0.0015*n` is one frame behind and misses the cap by a frame.
 *
 *   2. PAN DIVIDES BY `d`, NOT BY `d-1`, so the last output frame stops one step short of the
 *      right edge. Measured: with d=10 the window's left edge went 0,2,5,7,10,13,15,18,20,23 of a
 *      possible 26 -- exactly floor((iw-iw/z)*n/10).
 *
 * THE ORIGIN IS TRUNCATED TO A WHOLE SOURCE PIXEL, and that truncation IS mirrored. zoompan
 * computes `x` and `y` as doubles and then crops at integer coordinates, so a centred window is
 * centred to within one source pixel and no closer -- the server's own measurement calls this out
 * as the 0.6 px that survives its centring fix. In destination pixels it is at most `zoom/2` of
 * one source pixel (0.575 px at the cap), it is a pure translation, and it is exactly computable
 * from `z`, so reproducing it costs one `Math.floor` and removes the last systematic difference
 * between the preview's framing and the render's.
 *
 * What is deliberately NOT mirrored is the truncation of the window's SIZE (`w = floor(iw/z)`).
 * That one changes the effective magnification by up to 0.6 %, and reproducing it would make the
 * preview's zoom a function of the CANVAS's pixel width -- which is not the export's, because the
 * preview caps its backing store. A quantisation computed against the wrong grid is not a mirror;
 * it is a second, different judder. `source` below is how the exactly-computable half is kept
 * honest: pass the EXPORT frame's size and the integers are counted where ffmpeg counts them.
 */
export const SLOW_ZOOM_STEP = 0.0015;
export const MOTION_MAX_ZOOM = 1.15;
export const PAN_ZOOM = 1.15;

export type MotionKind = 'NONE' | 'SLOW_ZOOM' | 'PAN';

/**
 * What a NEW photo clip moves with: nothing. STILL is the default (QA round 2, Q5 -- the owner's
 * ruling, reversing S-30's `SLOW_ZOOM`, which both artboards had drawn selected). A photo the
 * user did not ask to move should not move; Slow zoom and Pan are one click away in the
 * inspector, and `COPY.motionExplainer` says so.
 *
 * This is the default for a clip BEING CREATED, applied by `hold-defaults.ts`'s
 * `defaultsForNewPhotoClip`, and it is written on the clip EXPLICITLY so the placement path never
 * has to know the schema's default. It happens to coincide with what an absent `motion` means
 * (the schema's default for a missing key is `NONE`, and `motionTransform` below treats
 * `undefined` as inert), so the wire projection omits it like every other default -- but the two
 * are separate facts, and changing this constant must never change a saved project.
 */
export const DEFAULT_PHOTO_MOTION: MotionKind = 'NONE';

export interface MotionTransform {
    zoom: number;
    /** Destination-pixel offset of the picture's centre, which is what `drawClip` applies. */
    panX: number;
    panY: number;
}

export const NO_MOTION: MotionTransform = Object.freeze({ zoom: 1, panX: 0, panY: 0 });

/**
 * The offset one axis needs, in DESTINATION pixels.
 *
 * `centre` is where the crop window's origin would sit if the window were exactly concentric with
 * the source; `origin` is where the template puts it. Both are in the source's own pixels -- the
 * 2W x 2H pre-scale ironman feeds zoompan (`FilterGraphBuilder.kt:200-201`) -- and the difference
 * between `centre` and the TRUNCATED origin is what the picture has to move by.
 *
 * `zoom/2` converts a source pixel to an export-frame pixel (the source is twice the frame);
 * `scale` converts an export-frame pixel to a canvas pixel, for a preview drawing smaller than the
 * frame it is previewing.
 */
function offsetFor(centre: number, origin: number, zoom: number, scale: number): number {
    return (centre - Math.floor(origin)) * (zoom / 2) * scale;
}

/**
 * @param motion      the clip's motion; `undefined` and `NONE` are both inert
 * @param frameIndex  the output frame inside this clip (0-based), from the frame grid
 * @param frameCount  the clip's length in output frames (`d` in the template)
 * @param destWidth   the width being drawn into, in canvas pixels
 * @param destHeight  the height being drawn into, in canvas pixels
 * @param source      the EXPORT FRAME's size -- the grid ffmpeg's integer origins are counted on.
 *                    Defaults to the destination, which is exact whenever the preview draws at the
 *                    frame's own size; when it does not, the residual is the sub-pixel truncation
 *                    above measured on a slightly different grid, which is smaller than the
 *                    truncation itself.
 */
export function motionTransform(
    motion: MotionKind | undefined,
    frameIndex: number,
    frameCount: number,
    destWidth: number,
    destHeight: number,
    source?: { width: number; height: number },
): MotionTransform {
    if (!motion || motion === 'NONE') return NO_MOTION;
    // A negative index is a clock that ran before its clip (a boundary rounds the wrong way); an
    // index past the end is the last frame held. Neither extrapolates: the export cannot.
    const n = Math.max(0, frameIndex);
    const sourceWidth = source && source.width > 0 ? source.width : destWidth;
    const sourceHeight = source && source.height > 0 ? source.height : destHeight;
    const scaleX = sourceWidth > 0 ? destWidth / sourceWidth : 1;
    const scaleY = sourceHeight > 0 ? destHeight / sourceHeight : 1;

    if (motion === 'SLOW_ZOOM') {
        const zoom = Math.min(1 + SLOW_ZOOM_STEP * (n + 1), MOTION_MAX_ZOOM);
        // `iw/2-(iw/zoom/2)` with `iw = 2 * sourceWidth` is `sourceWidth * (zoom-1) / zoom`: the
        // window is concentric with the source, so the only offset left is the truncation.
        const centreX = (sourceWidth * (zoom - 1)) / zoom;
        const centreY = (sourceHeight * (zoom - 1)) / zoom;
        return {
            zoom,
            panX: offsetFor(centreX, centreX, zoom, scaleX),
            panY: offsetFor(centreY, centreY, zoom, scaleY),
        };
    }

    // PAN. ffmpeg's `x` is the LEFT edge of the crop window inside the source: 0 at the first
    // frame (we see the left of the picture) and (iw - iw/z)*(n/d) after that. In canvas terms the
    // picture is scaled about the frame's centre, so "see the left" means "shift the picture
    // right" -- the offset starts at +half the overflow and walks toward -half without arriving.
    const zoom = PAN_ZOOM;
    const d = frameCount > 0 ? frameCount : 1;
    const progress = Math.min(1, n / d);
    const centreX = (sourceWidth * (zoom - 1)) / zoom;
    const originX = 2 * sourceWidth * (1 - 1 / zoom) * progress;
    const centreY = (sourceHeight * (zoom - 1)) / zoom;
    return {
        zoom,
        panX: offsetFor(centreX, originX, zoom, scaleX),
        // `(ih-ih/zoom)/2` is the same centre idiom in its other algebraic form, so the vertical
        // offset is the truncation and nothing else -- as it is for SLOW_ZOOM.
        panY: offsetFor(centreY, centreY, zoom, scaleY),
    };
}
